import React, { useState, useEffect } from 'react'
import {
  FormControl,
  FormHelperText,
  FilledInput,
  OutlinedInput,
  InputLabel,
  Input,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core'
import { withFormsy } from 'formsy-react'
import _ from '@lodash'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import { FormattedMessage } from 'react-intl'
import { arrayAsHookDependencies } from 'libs/hook'
import { StyleSelect } from 'app/components/selector/withStyleSelector'
import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
const useStyles = makeStyles((theme) => ({
  root: {
    color: theme.palette.secondary.main,
  },
  tooltipWidth: {
    width: '390px',
  },
}))

const NodeType = {
  parent: 'parent',
  child: 'child',
}

function CheckboxTreeFormsy(props) {
  const importedProps = _.pick(props, [
    'autoWidth',
    'children',
    'classes',
    'displayEmpty',
    'input',
    'inputProps',
    'MenuProps',
    'multiple',
    'native',
    'onClose',
    'onOpen',
    'open',
    'options',
    'renderValue',
    'SelectDisplayProps',
    'value',
    'variant',
  ])
  const classes = useStyles()

  // An error message is returned only if the component is invalid
  const errorMessage = props.errorMessage
  const value = props.value

  const currentValues = R.propOr(value, 'currentValues', props)

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!R.isNil(currentValues)) props.setValue(currentValues)
  }, [arrayAsHookDependencies(currentValues)])
  /* eslint-enable react-hooks/exhaustive-deps */

  const [isDisabledPanel, setIsDisabledPanel] = useState(false)

  const sortUniq = R.compose(R.sort(R.subtract), R.uniq)

  function input() {
    switch (importedProps.variant) {
      case 'outlined':
        return (
          <OutlinedInput
            labelWidth={R.length(props.label) * 8}
            id={props.name}
          />
        )
      case 'filled':
        return <FilledInput id={props.name} />
      default:
        return <Input id={props.name} />
    }
  }

  const formatStringToArrayNumber = (value) => value.split(',').map(Number)
  const isNotIncludes = R.complement(R.includes)
  const isNotEquals = R.complement(R.equals)

  function nextUncheckedState(type, oldValue, nextValue) {
    if (type === NodeType.parent) {
      return R.filter(
        isNotIncludes(R.__, formatStringToArrayNumber(nextValue)),
        oldValue,
      )
    }

    return oldValue.filter(isNotEquals(Number(nextValue)))
  }

  function nextCheckedState(type, oldValue, nextValue) {
    if (type === NodeType.parent) {
      return sortUniq([...oldValue, ...formatStringToArrayNumber(nextValue)])
    }

    return sortUniq([...oldValue, Number(nextValue)])
  }

  function getNextState(checked, type, oldValue, nextValue) {
    if (checked) {
      return nextCheckedState(type, oldValue, nextValue)
    }
    return nextUncheckedState(type, oldValue, nextValue)
  }

  const changeValue =
    (type = NodeType.child) =>
    (event) => {
      const oldValue = props.value
      const nextValue = event.target.value
      if (!R.equals(oldValue, nextValue)) {
        const nextState = getNextState(
          event.target.checked,
          type,
          oldValue,
          nextValue,
        )

        props.setValue(nextState)

        if (props.onChange) {
          props.onChange(nextState)
        }
      }
    }

  const shouldShowErrorMsg = Boolean(errorMessage) && !props.isPristine

  const DisplayLabel = (selected) => {
    if (importedProps.renderValue) return importedProps.renderValue(selected)

    const allChildren = importedProps.options.reduce((acc, cur) => {
      return [...acc, ...R.propOr([], 'children', cur)]
    }, [])

    return R.compose(
      R.join(', '),
      R.map((permissionId) => {
        return R.propOr(
          '',
          'name',
          allChildren.find(R.propEq('id', permissionId)),
        )
      }),
    )(selected)
  }

  return (
    <FormControl
      error={shouldShowErrorMsg}
      className={props.className}
      required={props.required}
      variant={importedProps.variant}
    >
      {props.label && (
        <InputLabel htmlFor={props.name}>{props.label}</InputLabel>
      )}
      <StyleSelect
        {...importedProps}
        value={currentValues}
        renderValue={DisplayLabel}
        input={input()}
      >
        {props.header &&
          props.header({
            changeValue,
            setValue: props.setValue,
            onChange: props.onChange,
            options: importedProps.options,
            setIsDisabledPanel,
            currentValues,
          })}
        {R.isEmpty(importedProps.options) ? (
          <div className={clsx('break-words px-4', classes.tooltipWidth)}>
            <FormattedMessage
              id={props.emptyLabelId || 'error.form.notFound'}
            />
          </div>
        ) : (
          importedProps.options.map((root) => {
            const rootValue = R.compose(
              R.when(R.isEmpty, R.always([root.id])),
              R.pluck('id'),
            )(R.propOr([], 'children', root))

            return (
              <div key={root.name}>
                <div>
                  <CheckboxLabel
                    label={root.name}
                    disabled={isDisabledPanel || Boolean(root.disabled)}
                    disableRipple
                    onClick={changeValue(NodeType.parent)}
                    checked={
                      R.all(R.includes(R.__, currentValues), rootValue) &&
                      !isDisabledPanel
                    }
                    key={root.name}
                    value={rootValue}
                  />
                </div>
                {R.compose(
                  R.map((child) => {
                    return (
                      <div key={child.name} className="ltr:pl-10 rtl:pr-10">
                        <CheckboxLabel
                          label={child.name}
                          disableRipple
                          onClick={changeValue(NodeType.child)}
                          checked={
                            currentValues.includes(child.id) && !isDisabledPanel
                          }
                          value={child.id}
                          disabled={R.compose(
                            R.not,
                            R.isNil,
                            R.find(R.equals(child.id)),
                            R.defaultTo([]),
                          )(props.disabledValues)}
                        />
                      </div>
                    )
                  }),
                  R.when(R.always(root.isChildrenHidden), R.always([])),
                  R.propOr([], 'children'),
                )(root)}
              </div>
            )
          })
        )}
      </StyleSelect>
      {shouldShowErrorMsg && <FormHelperText>{errorMessage}</FormHelperText>}
    </FormControl>
  )
}

const CheckboxLabel = ({ label, ...checkboxProps }) => {
  return (
    <FormControlLabel
      className="ltr:ml-1 rtl:mr-1"
      control={<Checkbox {...checkboxProps} />}
      label={label}
    />
  )
}

CheckboxTreeFormsy.propTypes = {
  emptyLabelId: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      children: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
            .isRequired,
          name: PropTypes.string.isRequired,
        }),
      ),
    }),
  ).isRequired,
  value: PropTypes.array,
  currentValues: PropTypes.array,
}

export default React.memo(withFormsy(CheckboxTreeFormsy))
